import { useEffect, useState } from 'react';
import Header from '@/components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getInquiries } from '@/services/inquiry';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from '@/hooks/use-toast';
import { ArrowLeft, Search, Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { PaginationControl } from '@/components/shared/PaginationControl';
import { pagination } from '@/constants';
import { MessageDialog } from '@/components/shared/MessageDialog';

interface Inquiry {
    _id: string;
    contactInfo: {
        name: string;
        email: string;
        phone: string;
    };
    user?: {
        name: string;
    };
    property: {
        specifications: {
            titleDeed: string;
        };
    };
    message: string;
    createdAt: string;
}

const BrokerLeads = () => {
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);

    // Debounce search query
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]);

    const fetchInquiries = async (page = pagination.page) => {
        setIsLoading(true);
        try {
            const { data, pagination: paginationData } = await getInquiries({
                page: page,
                limit: pagination.limit,
                searchQuery: debouncedSearchQuery
            });

            if (Array.isArray(data)) {
                setInquiries(data);
                if (paginationData) {
                    setTotalPages(paginationData.totalPages);
                }
            } else {
                console.error("Unexpected API response format:", data);
            }
        } catch (error) {
            console.error("Error fetching inquiries:", error);
            toast({
                title: "Error",
                description: "Failed to fetch leads.",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchInquiries();
    }, [debouncedSearchQuery]);



    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (e) {
            return 'Invalid Date';
        }
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <div className="pt-32 pb-20 bg-secondary/30 flex-1">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                        <div>
                            <h1 className="text-3xl font-bold">My Leads</h1>
                            <p className="text-muted-foreground mt-1">Manage your leads</p>
                        </div>
                        <Button asChild variant="outline">
                            <Link to="/broker/dashboard">
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back to Dashboard
                            </Link>
                        </Button>
                    </div>
                    <Card className="mb-8">
                        <CardHeader className="pb-3">
                            <CardTitle>Filters</CardTitle>
                            <CardDescription>Search and filter leads</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="text"
                                        placeholder="Search by name, email, or property..."
                                        className="pl-8"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <div className="rounded-md">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Phone</TableHead>
                                            <TableHead>Property</TableHead>
                                            <TableHead>Message</TableHead>
                                            <TableHead>Date</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {isLoading ? (
                                            [1, 2, 3, 4, 5].map((i) => (
                                                <TableRow key={i}>
                                                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                                    <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                                                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                                                </TableRow>
                                            ))
                                        ) : inquiries.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                    No leads found.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            inquiries.map((inquiry) => (
                                                <TableRow key={inquiry._id}>
                                                    <TableCell className="font-medium">
                                                        {inquiry.contactInfo?.name || inquiry.user?.name || 'Guest'}
                                                    </TableCell>
                                                    <TableCell>{inquiry.contactInfo?.email || 'N/A'}</TableCell>
                                                    <TableCell>{inquiry.contactInfo?.phone || 'N/A'}</TableCell>
                                                    <TableCell>
                                                        {inquiry.property?.specifications?.titleDeed || 'N/A'}
                                                    </TableCell>
                                                    <TableCell className="max-w-xs">
                                                        <div className="flex items-center">
                                                            <span className="truncate block max-w-[180px]" title={inquiry.message}>
                                                                {inquiry.message}
                                                            </span>
                                                            <MessageDialog message={inquiry.message} />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center text-sm">
                                                            <Calendar className="h-3 w-3 mr-2 text-muted-foreground" />
                                                            {formatDate(inquiry.createdAt)}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                    {/* Pagination */}
                    <div className="flex justify-center mt-4">
                        <PaginationControl
                            key={debouncedSearchQuery} // Reset on search
                            totalPages={totalPages}
                            onPageChange={fetchInquiries}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrokerLeads;
